import React, { useState, useEffect, Fragment } from "react";
import {
    Card, CardBody, Table, Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, Nav, NavItem, NavLink, TabContent, TabPane, Spinner,
    CardHeader, CardTitle, CardText
} from "reactstrap";
import classnames from "classnames";
import { Edit, Trash2, PlusCircle, Database, Server, HardDrive } from "react-feather";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import webservice from "../../../Service/webservice"; // Assuming webservice is configured elsewhere
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../Service/crypto';
import Swal from "sweetalert2";
import api from '../../../api';

interface AiModel {
    refid: number | string;
    modelName: string;
    rpm: number;
    tpm: number;
    rpd: number;
    batchEnqueuedTokens: number;
}

interface AiServerCost {
    mongoHour: string;
    mongoSec: string;
    mongoTax: string;
    awsHour: string;
    awsSec: string;
    awsTax: string;
    s3Month: string;
    s3Day: string;
    s3Tax: string;
    s3DownloadGb: string;
    s3DownloadTax: string;
}

interface PricingFormData {
    inputFree: string;
    inputPaid: string;
    outputFree: string;
    outputPaid: string;
    contextFree: string;
    contextPaid: string;
    groundingFree: string;
    groundingPaid: string;
    improveFree: string;
    improvePaid: string;
}

const initialFormData = {
    modelName: "",
    rpm: "",
    tpm: "",
    rpd: "",
    batchEnqueuedTokens: "",
};

const initialServerFormData: AiServerCost = {
    mongoHour: "0",
    mongoSec: "0",
    mongoTax: "0",
    awsHour: "0",
    awsSec: "0",
    awsTax: "0",
    s3Month: "0",
    s3Day: "0",
    s3Tax: "0",
    s3DownloadGb: "0",
    s3DownloadTax: "0",
};

const ModelLimitsScreen: React.FC = () => {
    // State management
    const [activeTab, setActiveTab] = useState<string>("1");
    const [modelList, setModelList] = useState<AiModel[]>([]);
    const [serverCostData, setServerCostData] = useState({
        mongo: { hour: 0, sec: 0, tax: 0 },
        aws: { hour: 0, sec: 0, tax: 0 },
        s3Storage: { month: 0, day: 0, tax: 0 },
        s3Download: { gb: 0, tax: 0 },
    });
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentItem, setCurrentItem] = useState<AiModel | null>(null);
    const [formData, setFormData] = useState(initialFormData);
    const [pricingModalOpen, setPricingModalOpen] = useState(false);
    const [serverModalOpen, setServerModalOpen] = useState(false);
    const [serverForm, setServerForm] = useState<AiServerCost>(initialServerFormData);
    const [isEditingPricing, setIsEditingPricing] = useState(false);
    const [pricingFormData, setPricingFormData] = useState<PricingFormData>({
        inputFree: '',
        inputPaid: '',
        outputFree: '',
        outputPaid: '',
        contextFree: '',
        contextPaid: '',
        groundingFree: '',
        groundingPaid: '',
        improveFree: '',
        improvePaid: ''
    });

    useEffect(() => {
        if (serverCostData) {
            setServerForm({
                mongoHour: String(serverCostData.mongo.hour),
                mongoSec: String(serverCostData.mongo.sec),
                mongoTax: String(serverCostData.mongo.tax),
                awsHour: String(serverCostData.aws.hour),
                awsSec: String(serverCostData.aws.sec),
                awsTax: String(serverCostData.aws.tax),
                s3Month: String(serverCostData.s3Storage.month),
                s3Day: String(serverCostData.s3Storage.day),
                s3Tax: String(serverCostData.s3Storage.tax),
                s3DownloadGb: String(serverCostData.s3Download.gb),
                s3DownloadTax: String(serverCostData.s3Download.tax),
            });
        }
    }, [serverCostData]);

    const fetchModels = async () => {
        setLoading(true);
        try {
            const key = await keyPromise;
            const response = await api.fetch('api/model_details/get', {
                method: 'GET'
            });
            const data = await response.json();
            const decryptedData = await AesDecrypt(key, data);
            setModelList(decryptedData);
        } catch (error) {
            console.error('Error fetching models:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModels();
    }, []);

    const fetchserver = async () => {
        setLoading(true);
        try {
            const key = await keyPromise;
            const response = await api.fetch('api/server_details/get', {
                method: 'GET'
            });
            const data = await response.json();
            const decryptedData = await AesDecrypt(key, data);

            setServerCostData({
                mongo: {
                    hour: decryptedData[0].mongoHour,
                    sec: decryptedData[0].mongoSec,
                    tax: decryptedData[0].mongoTax,
                },
                aws: {
                    hour: decryptedData[0].awsHour,
                    sec: decryptedData[0].awsSec,
                    tax: decryptedData[0].awsTax,
                },
                s3Storage: {
                    month: decryptedData[0].s3Month,
                    day: decryptedData[0].s3Day,
                    tax: decryptedData[0].s3Tax,
                },
                s3Download: {
                    gb: decryptedData[0].s3DownloadGb,
                    tax: decryptedData[0].s3DownloadTax,
                },
            });
        } catch (error) {
            console.error('Error fetching server data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchserver();
    }, []);

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const toggleModal = () => setModalOpen(!modalOpen);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleServerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setServerForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddNew = () => {
        setIsEditing(false);
        setCurrentItem(null);
        setFormData(initialFormData);
        toggleModal();
    };

    const handleEdit = (item: AiModel) => {
        setIsEditing(true);
        setCurrentItem(item);
        setFormData({
            modelName: item.modelName,
            rpm: String(item.rpm),
            tpm: String(item.tpm),
            rpd: String(item.rpd),
            batchEnqueuedTokens: String(item.batchEnqueuedTokens)
        });
        toggleModal();
    };

    const handleViewPricing = async (item: AiModel) => {
        setCurrentItem(item);
        setPricingModalOpen(true);
        setLoading(true);
        const Id = { refid: item.refid };
        try {
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, Id);
            const response = await api.fetch('api/modal_details/getpricing', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const data = await response.json();
            const decryptedData = await AesDecrypt(key, data);
            const modelPricing = decryptedData[0].pricing;

            if (modelPricing) {
                setPricingFormData({
                    inputFree: modelPricing.inputFree,
                    inputPaid: modelPricing.inputPaid,
                    outputFree: modelPricing.outputFree,
                    outputPaid: modelPricing.outputPaid,
                    contextFree: modelPricing.contextFree,
                    contextPaid: modelPricing.contextPaid,
                    groundingFree: modelPricing.groundingFree,
                    groundingPaid: modelPricing.groundingPaid,
                    improveFree: modelPricing.improveFree,
                    improvePaid: modelPricing.improvePaid,
                });
            }
        } catch (error) {
            console.error('Error fetching server data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number | string) => {
        const confirmResult = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });
        if (confirmResult.isConfirmed) {
            try {
                const Id = { id };
                const key = await keyPromise;
                const EncParams = await AesEncrypt(key, Id);
                const response = await api.fetch('api/model_details/delete', {
                    method: 'POST',
                    body: JSON.stringify({ EncParams })
                });
                const result = await response.json();
                const decryptedResult = await AesDecrypt(key, result);
                if (decryptedResult.message === 'Deleted successfully') {
                    Swal.fire({
                        title: 'Deleted!',
                        text: 'Item has been deleted successfully.',
                        icon: 'success',
                        confirmButtonText: 'OK',
                        customClass: {
                            confirmButton: 'btn btn-success',
                        },
                    });
                    setModelList(prevList => prevList.filter(item => item.refid !== id));
                }
            } catch (error) {
                console.error('Delete error:', error);
                Swal.fire({
                    title: 'Error!',
                    text: 'Something went wrong while deleting.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        }
    };

    const handleSaveModel = async (e: React.FormEvent) => {
        e.preventDefault();
        const modelToSave: AiModel = {
            refid: isEditing && currentItem ? currentItem.refid : new Date().getTime(),
            modelName: formData.modelName,
            rpm: Number(formData.rpm) || 0,
            tpm: Number(formData.tpm) || 0,
            rpd: Number(formData.rpd) || 0,
            batchEnqueuedTokens: Number(formData.batchEnqueuedTokens) || 0,
        };
        try {
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, modelToSave);
            const endpoint = isEditing ? 'edit' : 'add';
            const response = await api.fetch(`api/model_details/${endpoint}`, {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const result = await response.json();
            const decryptedResult = await AesDecrypt(key, result);
            if (decryptedResult.message === "Updated successfully" || decryptedResult.message === "Added successfully") {
                Swal.fire({
                    title: 'Success',
                    icon: 'success',
                    text: decryptedResult.message === "Added successfully" ? 'Added successfully' : 'Updated Successfully!',
                    allowOutsideClick: false,
                    customClass: {
                        confirmButton: 'btn btn-success',
                    },
                });
                if (isEditing) {
                    setModelList(prevList =>
                        prevList.map(item =>
                            item.refid === modelToSave.refid ? modelToSave : item
                        )
                    );
                } else {
                    setModelList(prevList => [...prevList, modelToSave]);
                }
            } else if (decryptedResult.message === "Model Name is already taken") {
                Swal.fire({
                    title: 'Info',
                    icon: 'info',
                    text: 'Model Name is already taken',
                    allowOutsideClick: false,
                    customClass: {
                        confirmButton: 'btn btn-success',
                    },
                });
            }
        } catch (error) {
            console.error('Save error:', error);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong while saving.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            toggleModal();
        }
    };

    const handleSaveServerCost = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, serverForm);
            const response = await api.fetch('api/server_details/edit', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const result = await response.json();
            const decryptedResult = await AesDecrypt(key, result);
            if (decryptedResult.message === 'Updated successfully') {
                setServerCostData({
                    mongo: {
                        hour: Number(serverForm.mongoHour),
                        sec: Number(serverForm.mongoSec),
                        tax: Number(serverForm.mongoTax),
                    },
                    aws: {
                        hour: Number(serverForm.awsHour),
                        sec: Number(serverForm.awsSec),
                        tax: Number(serverForm.awsTax),
                    },
                    s3Storage: {
                        month: Number(serverForm.s3Month),
                        day: Number(serverForm.s3Day),
                        tax: Number(serverForm.s3Tax),
                    },
                    s3Download: {
                        gb: Number(serverForm.s3DownloadGb),
                        tax: Number(serverForm.s3DownloadTax),
                    },
                });
                Swal.fire({
                    title: 'Updated Successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    customClass: {
                        confirmButton: 'btn btn-success',
                    },
                });
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: 'Something went wrong while updating.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            console.error('Error updating server costs:', error);
            Swal.fire({
                title: 'Error!',
                text: 'An error occurred while updating the server costs.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
        setServerModalOpen(false);
    };

    const handleEditPricing = () => {
        setIsEditingPricing(true);
    };

    const handlePricingInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setPricingFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSavePricing = async () => {
        const serverForm = {
            refid: currentItem?.refid,
            ...pricingFormData
        };
        try {
            const key = await keyPromise;
            const EncParams = await AesEncrypt(key, serverForm);
            const response = await api.fetch('api/model_details/update_pricing', {
                method: 'POST',
                body: JSON.stringify({ EncParams })
            });
            const result = await response.json();
            const decryptedResult = await AesDecrypt(key, result);
            if (decryptedResult.message === 'Pricing updated successfully') {
                setPricingFormData({
                    inputFree: serverForm.inputFree,
                    inputPaid: serverForm.inputPaid,
                    outputFree: serverForm.outputFree,
                    outputPaid: serverForm.outputPaid,
                    contextFree: serverForm.contextFree,
                    contextPaid: serverForm.contextPaid,
                    groundingFree: serverForm.groundingFree,
                    groundingPaid: serverForm.groundingPaid,
                    improveFree: serverForm.improveFree,
                    improvePaid: serverForm.improvePaid,
                });
                Swal.fire({
                    title: 'Updated Successfully!',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    customClass: {
                        confirmButton: 'btn btn-success',
                    },
                });
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: 'Something went wrong while updating.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (error) {
            console.error('Error updating Pricing costs:', error);
            Swal.fire({
                title: 'Error!',
                text: 'An error occurred while updating the Pricing costs.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
        setIsEditingPricing(false);
        setPricingModalOpen(false);
    };

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Costing" title="Limits & Costs" />
            <Card className="mt-3">
                <CardBody>
                    <Nav tabs className="custom-tab nav-primary mb-3">
                        <NavItem>
                            <NavLink className={classnames({ active: activeTab === "1" })} onClick={() => toggleTab("1")} style={{ cursor: "pointer" }}>
                                AI Model Rate Limits
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink className={classnames({ active: activeTab === "2" })} onClick={() => toggleTab("2")} style={{ cursor: "pointer" }}>
                                Server & Storage Cost
                            </NavLink>
                        </NavItem>
                    </Nav>
                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                            <Row className="mb-3 align-items-center">
                                <Col>
                                    <h3>AI Model Rate Limits</h3>
                                    <p className="text-muted">Manage rate limits for different AI models.</p>
                                </Col>
                                <Col className="text-end">
                                    <Button color="primary" onClick={handleAddNew}>
                                        <PlusCircle size={16} className="me-2" /> Add New Model
                                    </Button>
                                </Col>
                            </Row>
                            <Table bordered responsive hover>
                                <thead className="table-primary">
                                    <tr>
                                        <th><center>Model</center></th>
                                        <th><center>RPM</center></th>
                                        <th><center>TPM</center></th>
                                        <th><center>RPD</center></th>
                                        <th><center>Batch Enqueued Tokens</center></th>
                                        <th><center>Actions</center></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan={6} className="text-center p-5"><Spinner color="primary" /></td></tr>
                                    ) : modelList.length === 0 ? (
                                        <tr><td colSpan={6} className="text-center">No models found.</td></tr>
                                    ) : (
                                        modelList.map(item => (
                                            <tr key={item.refid}>
                                                <td>{item.modelName}</td>
                                                <td><center>{item.rpm.toLocaleString()}</center></td>
                                                <td><center>{item.tpm.toLocaleString()}</center></td>
                                                <td><center>{item.rpd > 0 ? item.rpd.toLocaleString() : "-"}</center></td>
                                                <td><center>{item.batchEnqueuedTokens > 0 ? item.batchEnqueuedTokens.toLocaleString() : "-"}</center></td>
                                                <td className="text-center">
                                                    <Button color="transparent" className="p-1" onClick={() => handleViewPricing(item)}>
                                                        <span className="badge bg-info text-white">View Pricing</span>
                                                    </Button>
                                                    <Button color="transparent" className="p-1" onClick={() => handleEdit(item)}>
                                                        <Edit size={18} className="text-info" />
                                                    </Button>
                                                    <Button color="transparent" className="p-1" onClick={() => handleDelete(item.refid)}>
                                                        <Trash2 size={18} className="text-danger" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </TabPane>
                        <TabPane tabId="2">
                            <Row className="mb-3 align-items-center">
                                <Col><h3>Infrastructure Costs</h3><p className="text-muted">Current pricing for underlying services.</p></Col>
                                <Col className="text-end">
                                    <Button color="info" onClick={() => setServerModalOpen(true)}>
                                        <Edit size={16} className="me-2" /> Edit Server Costs
                                    </Button>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6} lg={3} className="mb-4">
                                    <Card className="text-center h-100">
                                        <CardHeader className="bg-light-success text-success"><Database size={24} /><h5 className="mt-2 mb-0">MongoDB</h5></CardHeader>
                                        <CardBody>
                                            <CardTitle tag="h4">${serverCostData.mongo.hour}/hour</CardTitle>
                                            <CardText className="text-muted f-12">${serverCostData.mongo.sec}/sec</CardText>
                                            <CardText>Tax: {serverCostData.mongo.tax}%</CardText>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={6} lg={3} className="mb-4">
                                    <Card className="text-center h-100">
                                        <CardHeader className="bg-light-warning text-warning"><Server size={24} /><h5 className="mt-2 mb-0">AWS Compute</h5></CardHeader>
                                        <CardBody>
                                            <CardTitle tag="h4">${serverCostData.aws.hour}/hour</CardTitle>
                                            <CardText className="text-muted f-12">${serverCostData.aws.sec}/sec</CardText>
                                            <CardText>Tax: {serverCostData.aws.tax}%</CardText>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={6} lg={3} className="mb-4">
                                    <Card className="text-center h-100">
                                        <CardHeader className="bg-light-info text-info"><HardDrive size={24} /><h5 className="mt-2 mb-0">S3 Storage</h5></CardHeader>
                                        <CardBody>
                                            <CardTitle tag="h4">${serverCostData.s3Storage.month}/GB/month</CardTitle>
                                            <CardText className="text-muted f-12">${serverCostData.s3Storage.day}/GB/day</CardText>
                                            <CardText>Tax: {serverCostData.s3Storage.tax}%</CardText>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={6} lg={3} className="mb-4">
                                    <Card className="text-center h-100">
                                        <CardHeader className="bg-light-danger text-danger"><HardDrive size={24} /><h5 className="mt-2 mb-0">S3 Download</h5></CardHeader>
                                        <CardBody>
                                            <CardTitle tag="h4">${serverCostData.s3Download.gb}/GB</CardTitle>
                                            <CardText>Tax: {serverCostData.s3Download.tax}%</CardText>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </TabPane>
                    </TabContent>
                </CardBody>
            </Card>
            {/* Modal for Add/Edit AI Model Limits */}
            <Modal isOpen={modalOpen} toggle={toggleModal} centered>
                <ModalHeader toggle={toggleModal}>
                    {isEditing ? 'Edit Model Limits' : 'Add New Model'}
                </ModalHeader>
                <Form onSubmit={handleSaveModel}>
                    <ModalBody>
                        <FormGroup>
                            <Label for="modelName">Model Name</Label>
                            <Input id="modelName" name="modelName" placeholder="e.g., Gemini 3.0 Pro" value={formData.modelName} onChange={handleInputChange} required />
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="rpm">RPM (Requests Per Minute)</Label>
                                    <Input id="rpm" type="number" name="rpm" placeholder="e.g., 150" value={formData.rpm} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="tpm">TPM (Tokens Per Minute)</Label>
                                    <Input id="tpm" type="number" name="tpm" placeholder="e.g., 2000000" value={formData.tpm} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="rpd">RPD (Requests Per Day)</Label>
                                    <Input id="rpd" type="number" name="rpd" placeholder="e.g., 10000" value={formData.rpd} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="batchEnqueuedTokens">Batch Enqueued Tokens</Label>
                                    <Input id="batchEnqueuedTokens" type="number" name="batchEnqueuedTokens" placeholder="e.g., 5000000" value={formData.batchEnqueuedTokens} onChange={handleInputChange} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="success" type="submit">Save Changes</Button>
                        <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                    </ModalFooter>
                </Form>
            </Modal>
            {/* Modal for Edit Server Cost */}
            <Modal isOpen={serverModalOpen} toggle={() => setServerModalOpen(false)} size="lg">
                <ModalHeader toggle={() => setServerModalOpen(false)}>Edit Server & Storage Pricing</ModalHeader>
                <Form onSubmit={handleSaveServerCost}>
                    <ModalBody>
                        <h6>MongoDB</h6>
                        <Row>
                            <Col md={4}><FormGroup><Label>Price / Hour ($)</Label><Input type="number" step="any" name="mongoHour" value={serverForm.mongoHour} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Price / Sec ($)</Label><Input type="number" step="any" name="mongoSec" value={serverForm.mongoSec} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Tax (%)</Label><Input type="number" step="any" name="mongoTax" value={serverForm.mongoTax} onChange={handleServerInputChange} /></FormGroup></Col>
                        </Row>
                        <hr />
                        <h6>AWS Compute</h6>
                        <Row>
                            <Col md={4}><FormGroup><Label>Price / Hour ($)</Label><Input type="number" step="any" name="awsHour" value={serverForm.awsHour} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Price / Sec ($)</Label><Input type="number" step="any" name="awsSec" value={serverForm.awsSec} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Tax (%)</Label><Input type="number" step="any" name="awsTax" value={serverForm.awsTax} onChange={handleServerInputChange} /></FormGroup></Col>
                        </Row>
                        <hr />
                        <h6>S3 Storage</h6>
                        <Row>
                            <Col md={4}><FormGroup><Label>Price / GB / Month ($)</Label><Input type="number" step="any" name="s3Month" value={serverForm.s3Month} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Price / GB / Day ($)</Label><Input type="number" step="any" name="s3Day" value={serverForm.s3Day} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={4}><FormGroup><Label>Tax (%)</Label><Input type="number" step="any" name="s3Tax" value={serverForm.s3Tax} onChange={handleServerInputChange} /></FormGroup></Col>
                        </Row>
                        <hr />
                        <h6>S3 Download</h6>
                        <Row>
                            <Col md={6}><FormGroup><Label>Price / GB ($)</Label><Input type="number" step="any" name="s3DownloadGb" value={serverForm.s3DownloadGb} onChange={handleServerInputChange} /></FormGroup></Col>
                            <Col md={6}><FormGroup><Label>Tax (%)</Label><Input type="number" step="any" name="s3DownloadTax" value={serverForm.s3DownloadTax} onChange={handleServerInputChange} /></FormGroup></Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="success" type="submit">Save Changes</Button>
                        <Button color="secondary" onClick={() => setServerModalOpen(false)}>Cancel</Button>
                    </ModalFooter>
                </Form>
            </Modal>
            {/* Modal for Pricing Details with Edit Functionality */}
            <Modal isOpen={pricingModalOpen} toggle={() => setPricingModalOpen(false)} size="xl" centered>
                <ModalHeader toggle={() => setPricingModalOpen(false)}>
                    Pricing Details - {currentItem?.modelName}
                </ModalHeader>
                <ModalBody>
                    <Table bordered responsive>
                        <thead className="table-primary">
                            <tr>
                                <th>Category</th>
                                <th>Free Tier</th>
                                <th>Paid Tier</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Input Price</td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="inputFree" value={pricingFormData.inputFree} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.inputFree
                                    )}
                                </td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="textarea" name="inputPaid" value={pricingFormData.inputPaid} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.inputPaid.split('\n').map((line, i) => <div key={i}>{line}</div>)
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>Output Price (incl. thinking tokens)</td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="outputFree" value={pricingFormData.outputFree} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.outputFree
                                    )}
                                </td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="textarea" name="outputPaid" value={pricingFormData.outputPaid} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.outputPaid.split('\n').map((line, i) => <div key={i}>{line}</div>)
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>Context Caching Price</td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="contextFree" value={pricingFormData.contextFree} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.contextFree
                                    )}
                                </td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="textarea" name="contextPaid" value={pricingFormData.contextPaid} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.contextPaid.split('\n').map((line, i) => <div key={i}>{line}</div>)
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>Grounding with Google Search</td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="groundingFree" value={pricingFormData.groundingFree} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.groundingFree
                                    )}
                                </td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="groundingPaid" value={pricingFormData.groundingPaid} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.groundingPaid
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>Used to improve our products</td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="improveFree" value={pricingFormData.improveFree} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.improveFree
                                    )}
                                </td>
                                <td>
                                    {isEditingPricing ? (
                                        <Input type="text" name="improvePaid" value={pricingFormData.improvePaid} onChange={handlePricingInputChange} />
                                    ) : (
                                        pricingFormData.improvePaid
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </ModalBody>
                <ModalFooter>
                    {isEditingPricing ? (
                        <>
                            <Button color="success" onClick={handleSavePricing}>Save Changes</Button>
                            <Button color="secondary" onClick={() => setIsEditingPricing(false)}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            <Button color="info" onClick={handleEditPricing}>Edit</Button>
                            <Button color="secondary" onClick={() => setPricingModalOpen(false)}>Close</Button>
                        </>
                    )}
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ModelLimitsScreen;
